import { submitCinchChargeCardRequest } from './cinch-charge-card-request-service'
import styles from './cinchcharge-request-form.module.css'
import { CinchChargeRequestFormValues } from './types'
import {
  FormFieldError,
  Heading,
  Primary,
  SelectField,
  Text,
  TextField,
  TextLink,
} from '@cinch-labs/design-system'
import { TrackingEventTypes, useAnalytics } from '@cinch-nx/shared-analytics'
import cx from 'classnames'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

const defaultErrorMessage = 'You need to fill out this bit'
const selectOptions = [
  { label: 'Miss', value: 'Miss' },
  { label: 'Mrs', value: 'Mrs' },
  { label: 'Mr', value: 'Mr' },
  { label: 'Other', value: 'Other' },
]

interface CinchChargeRequestFormProps {
  setShowSuccessScreen: React.Dispatch<React.SetStateAction<boolean>>
}

export const CinchChargeRequestForm: React.FC<CinchChargeRequestFormProps> = ({
  setShowSuccessScreen,
}) => {
  const [showErrorMessage, setShowErrorMessage] = useState(false)
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<CinchChargeRequestFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      title: '',
      firstName: '',
      lastName: '',
      email: '',
      carRegistration: '',
      deliveryAddress: {
        addressLine1: '',
        addressLine2: '',
        town: '',
        county: '',
        postcode: '',
      },
    },
  })

  const submitData: SubmitHandler<CinchChargeRequestFormValues> = async (
    data,
  ) =>
    submitCinchChargeCardRequest(data)
      .then(() => {
        setShowSuccessScreen(true)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
      .catch(() => {
        setShowErrorMessage(true)
      })

  const { trackEvent } = useAnalytics()

  return (
    <div
      className={styles.mainSection}
      data-testid="cinchcharge-request-form-container"
    >
      <Heading className={styles.title} element="h1">
        Request a cinchCharge card
      </Heading>
      <Text className={styles.titleDescription}>
        Please fill this in with the details used to buy your car
      </Text>
      <form
        onSubmit={handleSubmit(submitData)}
        aria-labelledby="cinch-charge-request-card-form"
        data-testid="cinch-charge-request-card-form"
      >
        <SelectField
          id="title"
          label="Title"
          data-testid="title"
          options={selectOptions}
          {...register('title')}
          error={{
            show: !!errors.title,
            collapse: true,
            message: errors.title?.message,
          }}
          classNames={{
            container: cx(styles.fieldContainer, styles.selectfieldLabel),
          }}
        />
        <TextField
          label="First name"
          id="first-name"
          data-testid="first-name"
          {...register('firstName', {
            required: defaultErrorMessage,
          })}
          error={{
            show: !!errors.firstName,
            message: errors.firstName?.message,
            collapse: true,
          }}
          fullWidth
          classNames={{
            container: styles.fieldContainer,
            label: { label: styles.fieldLabel },
          }}
          maxLength={30}
        />
        <TextField
          label="Last name"
          id="last-name"
          data-testid="last-name"
          {...register('lastName', {
            required: defaultErrorMessage,
          })}
          error={{
            show: !!errors.lastName,
            message: errors.lastName?.message,
            collapse: true,
          }}
          fullWidth
          classNames={{
            container: styles.fieldContainer,
            label: { label: styles.fieldLabel },
          }}
          maxLength={30}
        />
        <TextField
          label="Email address"
          id="email-address"
          data-testid="email-address"
          type="email"
          {...register('email', { required: defaultErrorMessage })}
          error={{
            show: !!errors.email,
            message: errors.email?.message,
            collapse: true,
          }}
          fullWidth
          classNames={{
            container: styles.fieldContainer,
            label: { label: styles.fieldLabel },
          }}
          maxLength={50}
        />
        <TextField
          label="Car registration"
          id="car-registration"
          data-testid="car-registration"
          {...register('carRegistration', {
            required: defaultErrorMessage,
          })}
          error={{
            show: !!errors.carRegistration,
            message: errors.carRegistration?.message,
            collapse: true,
          }}
          fullWidth
          classNames={{
            label: { label: styles.fieldLabel },
          }}
          maxLength={8}
        />
        <Heading className={styles.subHeading}>Delivery address</Heading>
        <Text className={styles.subHeadingDescription}>
          The card will be sent to this address
        </Text>
        <TextField
          label="Address line 1"
          id="delivery-address-address-line-1"
          data-testid="delivery-address-address-line-1"
          {...register('deliveryAddress.addressLine1', {
            required: defaultErrorMessage,
          })}
          error={{
            show: !!errors.deliveryAddress?.addressLine1,
            message: errors.deliveryAddress?.addressLine1?.message,
            collapse: true,
          }}
          fullWidth
          classNames={{
            container: styles.fieldContainer,
            label: { label: styles.fieldLabel },
          }}
          maxLength={255}
        />
        <TextField
          label="Address line 2"
          id="delivery-address-address-line-2"
          data-testid="delivery-address-address-line-2"
          {...register('deliveryAddress.addressLine2')}
          error={{
            show: !!errors.deliveryAddress?.addressLine2,
            message: errors.deliveryAddress?.addressLine2?.message,
            collapse: true,
          }}
          fullWidth
          classNames={{
            container: styles.fieldContainer,
            label: { label: styles.fieldLabel },
          }}
          maxLength={255}
        />
        <TextField
          label="Town/city"
          id="delivery-address-town"
          data-testid="delivery-address-town"
          {...register('deliveryAddress.town', {
            required: defaultErrorMessage,
          })}
          error={{
            show: !!errors.deliveryAddress?.town,
            message: errors.deliveryAddress?.town?.message,
            collapse: true,
          }}
          fullWidth
          classNames={{
            container: styles.fieldContainer,
            label: { label: styles.fieldLabel },
          }}
          maxLength={35}
        />
        <TextField
          label="County"
          id="delivery-address-county"
          data-testid="delivery-address-county"
          {...register('deliveryAddress.county')}
          error={{
            show: !!errors.deliveryAddress?.county,
            message: errors.deliveryAddress?.county?.message,
            collapse: true,
          }}
          fullWidth
          classNames={{
            container: styles.fieldContainer,
            label: { label: styles.fieldLabel },
          }}
          maxLength={35}
        />
        <TextField
          label="Postcode"
          id="delivery-address-postcode"
          data-testid="delivery-address-postcode"
          {...register('deliveryAddress.postcode', {
            required: defaultErrorMessage,
          })}
          error={{
            show: !!errors.deliveryAddress?.postcode,
            message: errors.deliveryAddress?.postcode?.message,
            collapse: true,
          }}
          fullWidth
          classNames={{
            label: { label: styles.fieldLabel },
          }}
          maxLength={9}
        />
        {showErrorMessage && (
          <FormFieldError
            show={showErrorMessage}
            message="Request failed. Please try again. If the problem persists please contact hello@cinch.co.uk."
            classNames={{ container: styles.requestError }}
          />
        )}
        <Primary
          data-testid="request-submit-button"
          type={'submit'}
          isFullWidth
          isLoading={isSubmitting}
          classNames={{
            button: showErrorMessage
              ? styles.submitButtonWithError
              : styles.submitButton,
          }}
          onClick={() => {
            trackEvent({
              eventName: `cinchCharge card request form - Submit form click`,
              data: {
                event: {
                  name: 'click',
                  category: 'Request a cinchCharge card',
                  action: 'form CTA',
                  label: 'Submit request',
                  value: 'successful', // TODO: when API is hooked up, this value should be unsuccessful on api call failure
                },
              },
              type: TrackingEventTypes.ADOBE,
            })
          }}
        >
          Submit request
        </Primary>
        <Text className={styles.helperText} data-testid="helper-text">
          If we can't match your details to an order, our customer service team
          will get in touch by phone or email. We won't use these details for
          anything else. To see how cinch handles your personal data, view
          our&nbsp;
          <TextLink
            className={styles.linkText}
            href={'https://www.cinch.co.uk/policies/privacy'}
            target="_blank"
            onClick={() => {
              trackEvent({
                eventName: `cinchCharge card request form - Privacy Policy click`,
                data: {
                  event: {
                    name: 'click',
                    category: 'Request a cinchCharge card',
                    action: 'internal link',
                    label: 'Privacy Policy',
                  },
                },
                type: TrackingEventTypes.ADOBE,
              })
            }}
          >
            Privacy Policy
          </TextLink>
          .
        </Text>
      </form>
    </div>
  )
}
